import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  EXAM_TYPE_LABEL,
  PARTNER_TYPE_LABEL,
  STATION_LABEL,
  type AwaitingCheckIn,
  type CompletedEncounter,
  type HospitalQueue,
  type InProgressEncounter,
  type Partner,
} from '@dru/types';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button, ConfirmDialog, DataTable, EmptyState, EntityCombobox, ErrorState, IconAction, IconButton, RowActions, SearchInput, StatusBadge, cn, type Tone, MoreActions,
} from '@dru/ui';
import {
  Activity,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardCheck,
  Eye,
  LayoutGrid,
  List,
  Loader,
  RefreshCw,
  UserCheck,
  UserX,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { usePartner, useVenuePrefix } from '../usePartner';
import { ScanLookup } from '../components/ScanLookup';

function toISODateString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const TIME = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit' });
const DAY = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' });
/** "9:30 am" — the queue's one way of printing a time. */
export function clock(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : TIME.format(d);
}
/** A time, with the day in front when it is not the day being shown. */
function clockOn(v: string, day: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return toISODateString(d) === day ? TIME.format(d) : `${DAY.format(d)}, ${TIME.format(d)}`;
}

/** An examination type in words, never its key. */
export function examTypeLabel(key: string): string {
  if (EXAM_TYPE_LABEL[key as keyof typeof EXAM_TYPE_LABEL]) return EXAM_TYPE_LABEL[key as keyof typeof EXAM_TYPE_LABEL];
  const words = key.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A primary-looking link, for the one next step on a card or a row. */
const ACTION_LINK = 'inline-flex h-8 max-md:h-10 shrink-0 items-center justify-center gap-1.5 rounded-btn px-3 text-small font-semibold '
  + 'transition-colors duration-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1';
const PRIMARY_LINK = `${ACTION_LINK} bg-primary-600 text-white hover:bg-primary-700`;
const SECONDARY_LINK = `${ACTION_LINK} border border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50`;

/**
 * The front desk and floor coordinator's day.
 *
 * Three questions, in the order the desk asks them: who is expected and not
 * yet here, who is on the floor and how far along, who is done. Every person
 * carries the one next step for them — check in, open, view — so the screen
 * is a list of things to do rather than a list of records to read. Nothing
 * clinical is shown here: a station is done or not, never what it measured.
 */
export function Queue() {
  const { partner, partnerId, setPartner, search, fixed, loading: partnersLoading, error: venueError } = usePartner();
  const prefix = useVenuePrefix();
  const nav = useNavigate();
  const [nonce, setNonce] = useState(0);
  const { principal, permissions } = useAuth();
  const canNoShow = holds(permissions, 'appointment.noshow');
  const canCheckIn = holds(permissions, 'appointment.checkin');
  const [noShow, setNoShow] = useState<AwaitingCheckIn | null>(null);

  // Date selection state (YYYY-MM-DD)
  const todayStr = useMemo(() => toISODateString(new Date()), []);
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const isToday = selectedDate === todayStr;

  const [q, setQ] = useState('');
  const [viewMode, setViewMode] = useState<'board' | 'table'>('board');

  const shiftDay = (by: number) => {
    const current = new Date(`${selectedDate}T00:00:00`);
    current.setDate(current.getDate() + by);
    setSelectedDate(toISODateString(current));
  };

  const queue = useQuery<HospitalQueue>(
    () => (partnerId
      ? api.get(`/encounters/queue?partnerId=${partnerId}&limit=100&date=${selectedDate}`)
      : Promise.resolve({ awaitingCheckIn: [], inProgress: [], completed: [] })),
    [partnerId, nonce, selectedDate],
  );

  // Auto-refresh every 30 seconds while the tab is visible.
  useEffect(() => {
    if (!partnerId) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') setNonce((n) => n + 1);
    }, 30_000);
    return () => clearInterval(t);
  }, [partnerId]);

  const displayDate = useMemo(() => {
    const d = new Date(`${selectedDate}T00:00:00`);
    return Number.isNaN(d.getTime()) ? selectedDate : d.toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  }, [selectedDate]);

  const term = q.trim().toLowerCase();
  const matches = (e: { fullName: string; employeeCode: string }) => !term
    || e.fullName.toLowerCase().includes(term) || e.employeeCode.toLowerCase().includes(term);

  const awaiting = useMemo(
    () => (queue.data?.awaitingCheckIn ?? []).filter((a) => matches(a.employee)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queue.data?.awaitingCheckIn, term],
  );
  const inProgress = useMemo(
    () => (queue.data?.inProgress ?? []).filter((e) => matches(e.employee)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queue.data?.inProgress, term],
  );
  const completed = useMemo(
    () => (queue.data?.completed ?? []).filter((c) => matches(c.employee)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queue.data?.completed, term],
  );

  if (partnersLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-neutral-500">
        <RefreshCw className="mr-2 h-4 w-4 animate-spin text-primary-600" aria-hidden />
        Loading venue details…
      </div>
    );
  }

  // A venue's own login already names the venue in the top bar; saying it
  // again here is noise. The picker stays for company-bound accounts.
  const showVenue = !principal?.partner;
  const checkInHref = (appointmentId?: string) => `${prefix}/check-in${appointmentId ? `?appointment=${appointmentId}` : ''}`;
  // The desk takes a worker a day either side of their booking (the check-in
  // screen widens to find them), so the step is offered that far and no further.
  const dayGap = Math.round((new Date(`${selectedDate}T00:00:00`).getTime() - new Date(`${todayStr}T00:00:00`).getTime()) / 86_400_000);
  const checkInOffered = canCheckIn && Math.abs(dayGap) <= 1;
  const late = (a: AwaitingCheckIn) => isToday && new Date(a.scheduledFor).getTime() < Date.now();
  // Marking a no-show is for a slot that has passed; offering it ahead of
  // the slot invites marking the wrong person.
  const noShowOffered = (a: AwaitingCheckIn) => canNoShow && new Date(a.scheduledFor).getTime() < Date.now();

  const header = (
    <>
      <PageHeader
        className="mb-0"
        title={isToday ? 'Today’s queue' : 'Queue'}
        hint={isToday
          ? `${displayDate} · refreshes every 30 seconds`
          : `${displayDate} · ${selectedDate < todayStr ? 'a past day, for reference' : 'a future day'}`}
        actions={(
          <>
            {showVenue && <VenuePicker partner={partner} onChange={setPartner} search={search} fixed={fixed} />}
            {/* Under "More" on a phone: the list refreshes itself, and the
                one thing the desk does from here is check somebody in. */}
            <MoreActions>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setNonce((n) => n + 1)}
                loading={queue.loading}
                icon={<RefreshCw className="h-3.5 w-3.5" aria-hidden />}
              >
                Refresh
              </Button>
            </MoreActions>
            {canCheckIn && partnerId && (
              <Button size="sm" onClick={() => nav(checkInHref())} icon={<UserCheck className="h-3.5 w-3.5" aria-hidden />}>
                Check in an employee
              </Button>
            )}
          </>
        )}
      />

      {/* The day, the search and the view — one compact row. */}
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Choose the day" className="flex items-center gap-1">
          <IconButton label="Previous day" size="sm" variant="secondary" onClick={() => shiftDay(-1)}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </IconButton>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setSelectedDate(todayStr)}
            aria-pressed={isToday}
            className={cn(isToday && 'border-primary-500 bg-primary-50 text-primary-700 hover:bg-primary-50')}
          >
            Today
          </Button>
          <IconButton label="Next day" size="sm" variant="secondary" onClick={() => shiftDay(1)}>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </IconButton>
          <label className="ml-1">
            <span className="sr-only">Date</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
              className="h-8 max-md:h-10 rounded-input border border-neutral-300 bg-white px-2 text-small text-neutral-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </label>
        </div>

        <SearchInput
          value={q}
          onValueChange={setQ}
          placeholder="Name or employee code"
          aria-label="Search the queue"
          className="min-w-0 flex-[1_1_14rem] sm:max-w-xs"
        />

        <div role="group" aria-label="View" className="ml-auto inline-flex rounded-input border border-neutral-300 bg-white p-0.5">
          {([['board', 'Board', LayoutGrid], ['table', 'Table', List]] as const).map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              aria-pressed={viewMode === key}
              onClick={() => setViewMode(key)}
              className={cn(
                'inline-flex h-7 max-md:h-10 items-center gap-1.5 rounded px-2.5 text-caption font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                viewMode === key ? 'bg-primary-50 text-primary-700' : 'text-neutral-600 hover:text-neutral-900',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  if (!partnerId) {
    return (
      <div className="space-y-4">
        {header}
        {venueError ? (
          <ErrorState title="Could not load your venues" detail={venueError} />
        ) : (
          <EmptyState
            title="No venue selected"
            hint="Choose the hospital or laboratory you are working at to see its queue."
          />
        )}
      </div>
    );
  }

  const readyToClose = inProgress.filter((e) => e.pendingStations === 0).length;

  return (
    <div className="space-y-4">
      {header}

      {/* The fastest way in: scan the ID card or pass, land on the visit. */}
      {isToday && <ScanLookup partnerId={partnerId} prefix={prefix} canCheckIn={canCheckIn} />}

      {queue.error && !queue.data ? (
        <ErrorState title="Could not load the queue" detail={queue.error} onRetry={queue.reload} />
      ) : viewMode === 'board' ? (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
          <Lane
            title="Expected"
            hint="Booked, not yet checked in"
            icon={<UserCheck className="h-3.5 w-3.5" aria-hidden />}
            tone="info"
            count={awaiting.length}
            loading={queue.loading && !queue.data}
            empty={term ? 'Nobody expected matches the search.'
              : isToday ? 'Nobody left to check in today.' : 'Nobody is booked for this day.'}
          >
            {awaiting.map((a) => (
              <li key={a.appointmentId} className="rounded-card border border-neutral-200 bg-white p-3">
                <div className="flex items-start justify-between gap-2">
                  <Person name={a.employee.fullName} code={a.employee.employeeCode} />
                  <span className="shrink-0 text-right text-small font-medium tabular-nums text-neutral-800">
                    {clock(a.scheduledFor)}
                    {late(a) && <span className="block text-caption font-normal text-overdue-600">Slot passed</span>}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-caption text-neutral-600">{examTypeLabel(a.examinationType)}</span>
                  {/* One login serves every branch of the venue; without the
                      site a receptionist cannot tell who is due at their door. */}
                  {a.branch && (
                    <>
                      <span aria-hidden className="text-neutral-400">·</span>
                      <span className="text-caption font-medium text-neutral-800">{a.branch}</span>
                    </>
                  )}
                  <span aria-hidden className="text-neutral-400">·</span>
                  <ConsentBadge ready={a.consentReady} />
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-neutral-100 pt-2.5">
                  {noShowOffered(a) ? (
                    <button
                      type="button"
                      onClick={() => setNoShow(a)}
                      className="py-1 text-caption font-medium text-neutral-600 underline-offset-2 hover:text-danger-600 hover:underline"
                    >
                      Did not attend…
                    </button>
                  ) : <span />}
                  {checkInOffered && (
                    <Link to={checkInHref(a.appointmentId)} className={PRIMARY_LINK}>
                      Check in
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </Lane>

          <Lane
            title="On the floor"
            hint={readyToClose ? `${readyToClose} ready to close` : 'Checked in, stations running'}
            icon={<Activity className="h-3.5 w-3.5" aria-hidden />}
            tone="pending"
            count={inProgress.length}
            loading={queue.loading && !queue.data}
            empty={term ? 'Nobody on the floor matches the search.'
              // A station technician has no check-in: telling them to do it
              // sends them looking for a button they are not given.
              : canCheckIn ? 'Nobody is mid-examination. Check someone in to start their stations.'
                : 'Nobody is mid-examination. An employee appears here once the front desk checks them in.'}
          >
            {inProgress.map((e) => (
              <li key={e.encounterId} className="rounded-card border border-neutral-200 bg-white p-3">
                <div className="flex items-start justify-between gap-2">
                  <Person name={e.employee.fullName} code={e.employee.employeeCode} />
                  <span className="shrink-0 text-right text-caption tabular-nums text-neutral-600">
                    In at {clockOn(e.checkedInAt, selectedDate)}
                    {e.branch && <span className="block font-medium text-neutral-800">{e.branch}</span>}
                  </span>
                </div>
                <StationProgress stations={e.stations} />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-neutral-100 pt-2.5">
                  <span className={cn('text-caption', e.pendingStations === 0 ? 'font-semibold text-covered-600' : 'text-neutral-600')}>
                    {e.pendingStations === 0 ? 'All stations done — ready to close' : `${e.pendingStations} station${e.pendingStations === 1 ? '' : 's'} to go`}
                  </span>
                  <Link to={`${prefix}/encounter/${e.encounterId}`} className={e.pendingStations === 0 ? PRIMARY_LINK : SECONDARY_LINK}>
                    {e.pendingStations === 0 ? 'Open to close' : 'Open'}
                  </Link>
                </div>
              </li>
            ))}
          </Lane>

          <Lane
            title="Done"
            hint={isToday ? 'Closed today' : 'Closed on this day'}
            icon={<CheckCircle2 className="h-3.5 w-3.5" aria-hidden />}
            tone="covered"
            count={completed.length}
            loading={queue.loading && !queue.data}
            empty={term ? 'Nobody done matches the search.' : 'Encounters closed on this day appear here.'}
            compact
          >
            {completed.map((c) => (
              <li key={c.encounterId}>
                <Link
                  to={`${prefix}/encounter/${c.encounterId}`}
                  className="flex items-center justify-between gap-3 rounded-card border border-neutral-200 bg-white px-3 py-2 hover:border-neutral-300 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <Person name={c.employee.fullName} code={c.employee.employeeCode} />
                  <span className="flex shrink-0 flex-col items-end gap-1">
                    <Outcome c={c} />
                    <span className="text-caption tabular-nums text-neutral-500">{clockOn(c.completedAt, selectedDate)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </Lane>
        </div>
      ) : (
        <div className="space-y-6">
          <section className="space-y-2">
            <SectionTitle title="Expected" count={`${awaiting.length} booked, not checked in`} />
            <DataTable<AwaitingCheckIn>
              rows={awaiting}
              loading={queue.loading && !queue.data}
              error={queue.error}
              onRetry={queue.reload}
              rowKey={(a) => a.appointmentId}
              empty={{
                title: term ? 'Nobody expected matches the search' : 'Nobody is waiting',
                hint: 'Everyone booked for this day has been checked in, or nobody was booked.',
              }}
              columns={[
                {
                  key: 'employee', header: 'Employee', alwaysVisible: true,
                  cell: (a) => <Person name={a.employee.fullName} code={a.employee.employeeCode} />,
                },
                {
                  key: 'time', header: 'Slot',
                  cell: (a) => (
                    <span className="text-small tabular-nums text-neutral-800">
                      {clock(a.scheduledFor)}
                      {late(a) && <span className="ml-1.5 text-caption text-overdue-600">slot passed</span>}
                    </span>
                  ),
                },
                {
                  key: 'type', header: 'Examination',
                  cell: (a) => (
                    <span className="text-small text-neutral-700">
                      {examTypeLabel(a.examinationType)}
                      {a.branch && <span className="block text-caption font-medium text-neutral-800">{a.branch}</span>}
                    </span>
                  ),
                },
                { key: 'consent', header: 'Consent', cell: (a) => <ConsentBadge ready={a.consentReady} /> },
                {
                  key: 'go', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
                  cell: (a) => (
                    <RowActions>
                      {noShowOffered(a) && (
                        <IconAction label={`Mark ${a.employee.fullName} as did not attend`} icon={UserX} danger onClick={() => setNoShow(a)} />
                      )}
                      {checkInOffered && (
                        <IconAction label={`Check in ${a.employee.fullName}`} icon={UserCheck} primary onClick={() => nav(checkInHref(a.appointmentId))} />
                      )}
                    </RowActions>
                  ),
                },
              ]}
            />
          </section>

          <section className="space-y-2">
            <SectionTitle title="On the floor" count={`${inProgress.length} in progress`} />
            <DataTable<InProgressEncounter>
              rows={inProgress}
              loading={queue.loading && !queue.data}
              error={queue.error}
              onRetry={queue.reload}
              rowKey={(e) => e.encounterId}
              onRowClick={(e) => nav(`${prefix}/encounter/${e.encounterId}`)}
              empty={{
                title: term ? 'Nobody on the floor matches the search' : 'Nobody is mid-examination',
                hint: 'Check somebody in and their stations appear here.',
              }}
              columns={[
                {
                  key: 'employee', header: 'Employee', alwaysVisible: true,
                  cell: (e) => <Person name={e.employee.fullName} code={e.employee.employeeCode} />,
                },
                {
                  key: 'since', header: 'Checked in',
                  cell: (e) => <span className="text-small tabular-nums text-neutral-700">{clockOn(e.checkedInAt, selectedDate)}</span>,
                },
                { key: 'stations', header: 'Stations', cell: (e) => <StationProgress stations={e.stations} bare /> },
                {
                  key: 'left', header: 'Next',
                  cell: (e) => (
                    <span className={cn('text-small', e.pendingStations === 0 ? 'font-semibold text-covered-600' : 'text-neutral-700')}>
                      {e.pendingStations === 0 ? 'Ready to close' : `${e.pendingStations} to go`}
                    </span>
                  ),
                },
                {
                  key: 'go', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
                  cell: (e) => (
                    <RowActions>
                      {e.pendingStations === 0 ? (
                        <IconAction label={`Open ${e.employee.fullName}'s encounter to close it`} icon={ClipboardCheck} primary onClick={() => nav(`${prefix}/encounter/${e.encounterId}`)} />
                      ) : (
                        <IconAction label={`Open ${e.employee.fullName}'s encounter`} icon={Eye} onClick={() => nav(`${prefix}/encounter/${e.encounterId}`)} />
                      )}
                    </RowActions>
                  ),
                },
              ]}
            />
          </section>

          <section className="space-y-2">
            <SectionTitle title="Done" count={`${completed.length} closed`} />
            <DataTable<CompletedEncounter>
              rows={completed}
              loading={queue.loading && !queue.data}
              error={queue.error}
              onRetry={queue.reload}
              rowKey={(c) => c.encounterId}
              onRowClick={(c) => nav(`${prefix}/encounter/${c.encounterId}`)}
              empty={{
                title: term ? 'Nobody done matches the search' : 'Nothing closed yet',
                hint: 'Encounters closed on this day appear here.',
              }}
              columns={[
                {
                  key: 'employee', header: 'Employee', alwaysVisible: true,
                  cell: (c) => <Person name={c.employee.fullName} code={c.employee.employeeCode} />,
                },
                {
                  key: 'completedAt', header: 'Closed at',
                  cell: (c) => <span className="text-small tabular-nums text-neutral-700">{clockOn(c.completedAt, selectedDate)}</span>,
                },
                { key: 'status', header: 'Outcome', cell: (c) => <Outcome c={c} /> },
                {
                  key: 'go', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
                  cell: (c) => (
                    <RowActions>
                      <IconAction label={`View ${c.employee.fullName}'s encounter`} icon={Eye} onClick={() => nav(`${prefix}/encounter/${c.encounterId}`)} />
                    </RowActions>
                  ),
                },
              ]}
            />
          </section>
        </div>
      )}

      {noShow && (
        <ConfirmDialog
          title={`Mark ${noShow.employee.fullName} as did not attend?`}
          what={`Their ${clock(noShow.scheduledFor)} booking is closed as a no-show and they leave today’s list. They cannot be checked in against it afterwards.`}
          affects={`${noShow.employee.fullName} (${noShow.employee.employeeCode}) — ${examTypeLabel(noShow.examinationType)}. Their company’s HR is shown the missed booking and can rebook it.`}
          irreversible="Only a new booking from HR brings them back — the desk cannot undo this."
          confirmLabel="Mark did not attend"
          tone="danger"
          onCancel={() => setNoShow(null)}
          onConfirm={async () => {
            try {
              await api.post(`/appointments/${noShow.appointmentId}/no-show`, {});
              setNoShow(null);
              setNonce((n) => n + 1);
            } catch (e) {
              throw new Error(errorMessage(e));
            }
          }}
        />
      )}
    </div>
  );
}

const LANE_ICON: Record<'info' | 'pending' | 'covered', string> = {
  info: 'bg-info-100 text-info-600',
  pending: 'bg-pending-100 text-pending-600',
  covered: 'bg-covered-100 text-covered-600',
};

/** One column of the board: a heading with its count, then its people. */
function Lane({ title, hint, icon, tone, count, loading, empty, compact, children }: {
  title: string;
  hint: string;
  icon: ReactNode;
  tone: 'info' | 'pending' | 'covered';
  count: number;
  loading: boolean;
  empty: string;
  compact?: boolean;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex min-w-0 flex-col rounded-card border border-neutral-200 bg-neutral-50 p-3">
      <div className="mb-3 flex items-center justify-between gap-2 px-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full', LANE_ICON[tone])}>{icon}</span>
          <div className="min-w-0">
            <h2 className="text-small font-semibold text-neutral-900">{title}</h2>
            <p className="text-caption text-neutral-500">{hint}</p>
          </div>
        </div>
        <span className={cn('rounded-badge px-2 py-0.5 text-caption font-semibold tabular-nums', LANE_ICON[tone])}>
          {loading ? '…' : count}
          <span className="sr-only"> {title.toLowerCase()}</span>
        </span>
      </div>
      {loading ? (
        <div className="space-y-2" aria-busy="true" aria-label="Loading">
          {[0, 1].map((i) => <div key={i} className="h-20 animate-pulse rounded-card bg-neutral-100" />)}
        </div>
      ) : count === 0 ? (
        <p className="rounded-card border border-dashed border-neutral-300 px-4 py-6 text-center text-caption text-neutral-500">
          {empty}
        </p>
      ) : (
        <ul className={cn(compact ? 'space-y-1.5' : 'space-y-2')}>{children}</ul>
      )}
    </section>
  );
}

function SectionTitle({ title, count }: { title: string; count: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <h2 className="text-h4 text-neutral-900">{title}</h2>
      <span className="text-caption text-neutral-500">{count}</span>
    </div>
  );
}

function Person({ name, code }: { name: string; code: string }) {
  return (
    <div className="min-w-0">
      {/* Wraps rather than truncates: on a tablet's board lane "Sachi…" is nobody. */}
      <div className="break-words text-small font-medium text-neutral-900">{name}</div>
      <div className="break-all font-mono text-caption text-neutral-500">{code}</div>
    </div>
  );
}

function ConsentBadge({ ready }: { ready: boolean }) {
  return ready
    ? <StatusBadge tone="covered">Consent given</StatusBadge>
    : <StatusBadge tone="dueSoon">Consent needed at desk</StatusBadge>;
}

const STATION_STATE: Record<string, { word: string; tone: Tone }> = {
  COMPLETED: { word: 'done', tone: 'covered' },
  IN_PROGRESS: { word: 'in progress', tone: 'pending' },
  SKIPPED: { word: 'skipped', tone: 'neutral' },
  PENDING: { word: 'not started', tone: 'neutral' },
};

/**
 * Which stations are done, as a row of named chips. Each carries a mark as
 * well as a colour, so "done" does not rest on telling green from grey.
 */
function StationProgress({ stations, bare }: {
  stations: Array<{ station: string; status: string }>;
  bare?: boolean;
}) {
  const done = stations.filter((s) => s.status === 'COMPLETED').length;
  return (
    <div className={cn(!bare && 'mt-2.5')}>
      {!bare && (
        <div className="mb-1 text-caption text-neutral-600">
          {done} of {stations.length} station{stations.length === 1 ? '' : 's'} done
        </div>
      )}
      <ul className="flex flex-wrap gap-1" aria-label="Stations">
        {stations.map((s) => {
          const st = STATION_STATE[s.status] ?? STATION_STATE.PENDING;
          const Icon = s.status === 'COMPLETED' ? Check : s.status === 'IN_PROGRESS' ? Loader : Circle;
          return (
            <li key={s.station}>
              <StatusBadge tone={st.tone}>
                <Icon className="h-3 w-3" aria-hidden />
                {STATION_LABEL[s.station] ?? s.station}
                <span className="sr-only"> — {st.word}</span>
              </StatusBadge>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * How a closed encounter ended. A partial close leaves the case in progress
 * — it has not gone on to the laboratory or the physician — and the desk
 * should see that without opening it.
 */
function Outcome({ c }: { c: CompletedEncounter }) {
  const partial = c.orderStatus === 'IN_PROGRESS' || c.orderStatus === 'CHECKED_IN';
  if (partial) return <StatusBadge tone="dueSoon">Closed partial</StatusBadge>;
  return <StatusBadge tone="covered">Complete</StatusBadge>;
}

/**
 * Which venue's day this is.
 */
export function VenuePicker({
  partner,
  onChange,
  search,
  label = 'Venue',
  fixed = false,
}: {
  partner: Partner | null;
  onChange: (p: Partner | null) => void;
  search: (q: string, cursor: string | null) => Promise<{ items: Partner[]; nextCursor: string | null }>;
  label?: string;
  fixed?: boolean;
}) {
  if (fixed) {
    return (
      <div className="flex min-w-0 items-center gap-2 text-small text-neutral-600">
        <span className="shrink-0 text-neutral-500">{label}:</span>
        <span className="truncate font-medium text-neutral-900">{partner?.name ?? '—'}</span>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center gap-2 sm:w-auto">
      <span className="shrink-0 text-xs text-neutral-500">{label}:</span>
      <EntityCombobox<Partner>
        className="min-w-0 flex-1 sm:w-64 sm:flex-none"
        value={partner}
        onChange={onChange}
        fetchOptions={search}
        getKey={(p) => p.id}
        placeholder="Choose a venue…"
        searchPlaceholder="Hospital name, code or city"
        emptyMessage="No empanelled venue matches that."
        renderOption={(p) => (
          <>
            <span className="block font-medium text-neutral-900">{p.name}</span>
            <span className="block text-xs font-normal text-neutral-500">
              {[p.code, p.city, p.type ? PARTNER_TYPE_LABEL[p.type] ?? p.type : undefined]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </>
        )}
        renderValue={(p) => `${p.name}${p.city ? ` — ${p.city}` : ''}`}
      />
    </div>
  );
}
